import { Registration, REGISTRATION_STATUS_LABELS, Webinar } from "./types";
import { formatDateTime } from "./format";
import { DeliveriesByRegistration, deliveryLabel } from "./emailStatus";

// Only what the administrator needs. Internal IDs, timestamps like updatedAt and the
// privacy-notice version stay out of the export.
const HEADERS = [
  "Registration ID",
  "First name",
  "Surname",
  "Email",
  "Phone",
  "Organisation",
  "Professional role",
  "Webinar",
  "Webinar date (SAST)",
  "Registered on (SAST)",
  "Status",
  "Marketing consent",
  "Confirmation email",
  "Joining instructions email",
  "Joining instructions sent by hand (SAST)",
  "Archived on (SAST)",
];

/**
 * Every cell is quoted, with inner quotes doubled. Cells starting with = + - @ get a leading
 * apostrophe so spreadsheet apps show them as text rather than running them as formulas.
 */
const cell = (value: string | undefined) => {
  let text = value ?? "";
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

export function registrationsToCsv(
  registrations: Registration[],
  webinarsById: Record<string, Webinar>,
  deliveries: DeliveriesByRegistration = {},
): string {
  const rows = registrations.map(r => {
    const webinar = webinarsById[r.webinarId];
    return [
      r.reference,
      r.firstName,
      r.lastName,
      r.email,
      r.phone,
      r.organisation,
      r.professionalRole,
      webinar ? webinar.title : "Unknown webinar",
      // Archived registrations show the date of their own round, not the webinar's latest date
      r.roundStartAt ? formatDateTime(r.roundStartAt) : webinar ? formatDateTime(webinar.startAt) : "",
      formatDateTime(r.registeredAt),
      REGISTRATION_STATUS_LABELS[r.status],
      r.marketingConsent ? "Yes" : "No",
      deliveryLabel(deliveries[r.id]?.confirmation),
      deliveryLabel(deliveries[r.id]?.joining_instructions),
      r.instructionsSentAt ? formatDateTime(r.instructionsSentAt) : "",
      r.archivedAt ? formatDateTime(r.archivedAt) : "",
    ].map(cell).join(",");
  });
  // The byte-order mark makes Excel read the file as UTF-8 (names with accents stay intact)
  return "﻿" + [HEADERS.map(cell).join(","), ...rows].join("\r\n");
}

/** Saves CSV text as a file through the browser's download */
export function downloadCsv(csv: string, filename: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
